// Provisional (Phase 1) copy. Final language arrives in Phase 3.

export const ORDO_STATEMENT =
  "General Ordo is a governing architecture for how intelligence understands situations, exercises consequential judgment, participates in human arrangements, and remains answerable to changing reality.";

export const ORDO_READING = [
  "As intelligence becomes capable of doing more, the structure through which it understands a situation, exercises judgment, and acts within human arrangements matters more than any single capability.",
  "General Ordo addresses five connected responsibilities: evidence, judgment, authority, action, and continuing standing. Each concerns a different question an intelligent system must be able to answer for. Together they describe how consequential judgment can remain answerable to the people it affects and to a reality that keeps changing.",
  "The architecture has two kinds of expression. It is applied in the Ordo software family, Ordo Engine and Ordo Stewardship, and it may be incorporated, as infrastructure, into intelligent systems developed by other organizations. Breakpoint and Mesh are related but separate capabilities that draw on the same foundation.",
  "This public page describes responsibilities and relationships, not mechanisms. Detailed specifications are not published here; deeper examination can be arranged on an appropriately scoped basis.",
];

export const RESPONSIBILITY_IDS = ["evidence", "judgment", "authority", "action", "standing"];

export const GROUP_ORDER = ["whole", "responsibility", "family", "licensing", "related"];

export const GROUP_LABELS = {
  whole: "Governing architecture",
  responsibility: "Governing responsibility",
  family: "Ordo software family",
  licensing: "Separate licensing route",
  related: "Related applied capability · separate offering",
};

export const ORDO_NODES = {
  whole: {
    id: "whole", group: "whole", label: "General Ordo", tagline: "The governing architecture as a whole",
    concerns: "How intelligence understands situations, exercises consequential judgment, participates in human arrangements, and remains answerable to changing reality.",
    matters: "As AI becomes capable of doing more, these structures, not capability alone, determine whether its participation in human arrangements can be relied upon.",
    relates: ["evidence", "judgment", "authority", "action", "standing", "infrastructure"],
    relationNote: "Five governing responsibilities, applied through the Ordo software family and available separately as infrastructure.",
    to: "/ordo#expressions", toLabel: "See every expression",
  },
  evidence: {
    id: "evidence", group: "responsibility", index: "01", label: "Evidence", tagline: "What is actually known",
    concerns: "What is known about a situation, where that knowledge comes from, and how well it holds.",
    matters: "Consequential judgment can be no more answerable than the evidence it rests on.",
    relates: ["judgment", "standing", "engine", "breakpoint"],
    relationNote: "Evidence feeds judgment, and is reopened whenever continuing standing is questioned.",
    to: "/ordo/engine", toLabel: "Explore in Ordo Engine",
  },
  judgment: {
    id: "judgment", group: "responsibility", index: "02", label: "Judgment", tagline: "What movement is warranted",
    concerns: "How a present consequential situation is examined and what movement it actually warrants.",
    matters: "As intelligence is able to do more, the quality and answerability of its judgment becomes decisive.",
    relates: ["evidence", "authority", "engine"],
    relationNote: "Judgment works from evidence and must operate within authority.",
    to: "/ordo/engine", toLabel: "Explore in Ordo Engine",
  },
  authority: {
    id: "authority", group: "responsibility", index: "03", label: "Authority", tagline: "Who may decide and act",
    concerns: "Who or what is entitled to decide or act, on whose behalf, and within which limits.",
    matters: "Capable intelligence acting without clear authority places human arrangements at risk.",
    relates: ["judgment", "action", "engine", "mesh"],
    relationNote: "Authority bounds both judgment and the action it permits.",
    to: "/ordo/engine", toLabel: "Explore in Ordo Engine",
  },
  action: {
    id: "action", group: "responsibility", index: "04", label: "Action", tagline: "Movement carried into the world",
    concerns: "How warranted movement is carried into a human arrangement by people, systems, or both.",
    matters: "Action is where judgment becomes consequential for others.",
    relates: ["authority", "standing", "engine", "mesh"],
    relationNote: "Action follows authority, and what it establishes must continue to hold.",
    to: "/ordo/engine", toLabel: "Explore in Ordo Engine",
  },
  standing: {
    id: "standing", group: "responsibility", index: "05", label: "Continuing standing", tagline: "Remaining answerable over time",
    concerns: "Whether established arrangements retain or lose standing as circumstances change.",
    matters: "What was sound when established may not remain sound. Intelligence must stay answerable to changing reality.",
    relates: ["action", "evidence", "stewardship"],
    relationNote: "Continuing standing returns to evidence when circumstances change.",
    to: "/ordo/stewardship", toLabel: "Explore in Ordo Stewardship",
  },
  infrastructure: {
    id: "infrastructure", group: "licensing", label: "General Ordo Infrastructure", tagline: "Separate licensing route",
    concerns: "How the governing architecture could be incorporated into intelligent systems developed by other organizations.",
    matters: "Organizations building intelligent systems may need a governing architecture rather than another application.",
    relates: ["whole"],
    relationNote: "Infrastructure carries the architecture as a whole into other systems. It is not a fifth applied software product.",
    to: "/ordo/infrastructure", toLabel: "Explore Infrastructure",
  },
  engine: {
    id: "engine", group: "family", label: "Ordo Engine", tagline: "Present judgment",
    concerns: "Examining present consequential situations and determining what movement is warranted.",
    matters: "Present consequential judgment is where capable intelligence most directly affects people.",
    relates: ["evidence", "judgment", "authority", "action", "stewardship"],
    relationNote: "Engine applies the first four responsibilities to a present situation; Stewardship carries what it establishes forward.",
    to: "/ordo/engine", toLabel: "Explore Ordo Engine",
  },
  stewardship: {
    id: "stewardship", group: "family", label: "Ordo Stewardship", tagline: "Continuing standing",
    concerns: "How established arrangements retain or lose standing as circumstances change.",
    matters: "Arrangements are often made once and left to drift. Something must keep asking whether they still hold.",
    relates: ["standing", "evidence", "engine"],
    relationNote: "Stewardship applies continuing standing, and returns arrangements to evidence when reality moves.",
    to: "/ordo/stewardship", toLabel: "Explore Ordo Stewardship",
  },
  breakpoint: {
    id: "breakpoint", group: "related", label: "Breakpoint", tagline: "Consequential recognition",
    concerns: "Recognising when something consequential has occurred within ordinary work.",
    matters: "Consequence often goes unrecognised. Recognition is a responsibility of its own.",
    relates: ["evidence", "engine"],
    relationNote: "A separate, independently installable offering. It contributes a distinct recognition responsibility and is not a subdivision of General Ordo.",
    to: "/enterprise/breakpoint", toLabel: "Explore Breakpoint",
  },
  mesh: {
    id: "mesh", group: "related", label: "Mesh", tagline: "Connected participation",
    concerns: "How people and responsibilities connect and participate in shared work.",
    matters: "Shared work fragments when participation is not connected.",
    relates: ["authority", "action"],
    relationNote: "A separate, independently installable offering. It contributes a distinct participation responsibility and is not a subdivision of General Ordo.",
    to: "/enterprise/mesh", toLabel: "Explore Mesh",
  },
};
